import { CqlParameter } from "./../net/CqlParameter";
import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { FeatureGeoJson } from "../FeatureGeoJson";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { Feature, Map } from "ol";
import ImageWMSEx from "../net/ImageWMSEx";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { IAttributeConvert } from "@/attribute/convert/IAttributeConvert";
import { AttributeType } from "../define/AttributeType";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { getLayerKey } from "../LayerName";
import { LayerUtil } from "../util/LayerUtil";
import { GeneralTrafficLocationAttributeConvert } from "@/attribute/convert/GeneralTrafficLocationAttributeConvert";
import { GeneralTrafficTimeAttributeConvert } from "@/attribute/convert/GeneralTrafficTimeAttributeConvert";
import { FeatureType } from "../define/FeatureType";
import { GeneralTrafficLayerName } from "./GeneralTrafficLayerName";
import { GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST } from "@/components/define/defineType";

/**
 * 最大ズームレベル
 */

const LAYER_MAX_ZOOMLEVEL = 18;

/**
 * 最小ズームレベル
 */

const LAYER_MIN_ZOOMLEVEL = 11;

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

type AttributeConvertInfo = {
  contextMenu: string;
  attributeConvert: IAttributeConvert;
  selectedLayerStyle?: string;
};

/**
 * 属性変換クラス取得関数
 */
const FUNCTION_getRoadkillAttributeConvert = (
  key: string,
  feature: Feature
): AttributeConvertInfo => {
  switch (key) {
    /** 箇所別基本表 */
    case GETMAP_LAYERNAME_MAP[0].physical:
      return {
        contextMenu: FeatureType.GeneralTrafficLocation,
        attributeConvert: new GeneralTrafficLocationAttributeConvert(feature),
        selectedLayerStyle: GETMAP_LAYERNAME_MAP[0].physical + "_selected",
      };
    /** 時間帯別交通量 */
    case GETMAP_LAYERNAME_MAP[1].physical:
      return {
        contextMenu: FeatureType.GeneralTrafficTime,
        attributeConvert: new GeneralTrafficTimeAttributeConvert(feature),
        selectedLayerStyle: GETMAP_LAYERNAME_MAP[1].physical + "_selected",
      };
  }
};

/**
 * GetMap用レイヤ物理名マッピング
 */
const GETMAP_LAYERNAME_MAP = [
  {
    physical: "t_general_traffic_location_base",
    logical: FeatureType.GeneralTrafficLocation,
  },
  {
    physical: "t_general_traffic_time_base",
    logical: FeatureType.GeneralTrafficTime,
  },
];
/**
 * 一般交通量調査データレイヤ出力クラス
 */
export class GeneralTrafficLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMSEx>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMSEx;

  /**
   * 表示対象レイヤ
   */
  private _visibleLayers: GeneralTrafficLayerName[] = [];

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * レイヤ定義を取得
   * @param map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMSEx>[] {
    if (this._wmsLayer) {
      return [this._wmsLayer];
    }
    //WMSレイヤの取得
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: "",
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
      maxZoom: LAYER_MAX_ZOOMLEVEL,
      minZoom: LAYER_MIN_ZOOMLEVEL,
    });
    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }

    const cqlParameters = this._visibleLayers.map((layerDetail) => {
      let cqlParameter = `"道路種別コード" IN (${layerDetail.roadCategory
        .map((ele) => "'" + ele + "'")
        .join(",")})`;
      if (layerDetail.tableType === FeatureType.GeneralTrafficTime) {
        cqlParameter = cqlParameter.concat(
          ` AND "hour" = ${layerDetail.specificationTime}`
        );
      }
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameter
      );
    });
    const buffer = 5;
    const WMSGetter = new WMSGetFeatureManager();
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width,
      buffer
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];

        const selectedId = `${layerKey}.${feature.get("ジオメトリID")}`;

        const wfsCqlParameterString = `"ジオメトリID"='${feature.get(
          "ジオメトリID"
        )}'`;
        const wfsCqlParameters = [
          new CqlParameter(layerKey, wfsCqlParameterString),
        ];
        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          wfsCqlParameters
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });
        selectFeature.setId(selectedId);
        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, selectedId);
        const attributeConvertInfo = FUNCTION_getRoadkillAttributeConvert(
          layerKey,
          wfsFeature
        );
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          AttributeType.Normal,
          attributeConvertInfo.attributeConvert as IAttributeConvert,
          true,
          layerKey,
          attributeConvertInfo.selectedLayerStyle,
          selectedId
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 指定範囲の地物情報を取得
   * @param map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    if (
      map.getView().getZoom() > LAYER_MAX_ZOOMLEVEL ||
      map.getView().getZoom() <= LAYER_MIN_ZOOMLEVEL
    ) {
      return null;
    }
    // レイヤ論理名を抽出
    const cqlParameters = this._visibleLayers.map((layerDetail) => {
      let layerKey = GETMAP_LAYERNAME_MAP.find(
        (obj) => obj.logical === layerDetail.getLayerName()
      ).physical;
      if (
        layerDetail.tableType === FeatureType.GeneralTrafficTime ||
        [
          GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.morningEveningSpeed,
          GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.daytimeSpeed,
        ].includes(layerDetail.dataType)
      ) {
        layerKey = layerKey.replaceAll("t_", "v_");
      }
      const cqlParameterString = `"道路種別コード" IN (${layerDetail.roadCategory
        .map((ele) => "'" + ele + "'")
        .join(",")})`;
      return new CqlParameter(
        getLayerKey(layerDetail.getLayerName()),
        cqlParameterString
      );
    });
    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = selectedFeatures.map(
      (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];
        // 属性変換クラス・attributeクラスを生成
        let attributeConvertInfo: AttributeConvertInfo = null;
        const attributeType: AttributeType = AttributeType.Normal;

        attributeConvertInfo = FUNCTION_getRoadkillAttributeConvert(
          layerKey,
          feature
        );

        // selectFeatureを生成（地物情報のみ）
        const geometry = feature.getGeometry();

        const selectFeature = new Feature({
          geometry,
        });

        const geoJSON = new GeoJSON().writeFeature(feature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert as IAttributeConvert,
          false
        );
        return selectedFeatureInfo;
      }
    );
    return selectFeatureInfoList;
  }

  /**
   * 地物属性の有無を取得
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number
  ): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param layerNameList 対象ロードキルリスト
   */
  setVisible(isVisible: boolean, layerNameList: GeneralTrafficLayerName[]) {
    this._visibleLayers = layerNameList;
    const layerKeys: string[] = [];
    const cqlParameters: string[] = [];
    const styles: string[] = [];
    if (isVisible && this._visibleLayers.length !== 0) {
      this._visibleLayers.forEach((layerDetail) => {
        let layerKey = GETMAP_LAYERNAME_MAP.find(
          (obj) => obj.logical === layerDetail.getLayerName()
        ).physical;
        if (
          layerDetail.tableType === FeatureType.GeneralTrafficTime ||
          [
            GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.morningEveningSpeed,
            GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.daytimeSpeed,
          ].includes(layerDetail.dataType)
        ) {
          layerKey = layerKey.replaceAll("t_", "v_");
        }

        let cqlParameter = `"道路種別コード" IN (${layerDetail.roadCategory
          .map((ele) => "'" + ele + "'")
          .join(",")})`;

        if (layerDetail.tableType === FeatureType.GeneralTrafficTime) {
          cqlParameter = cqlParameter.concat(
            ` AND "hour" = ${layerDetail.specificationTime}`
          );
        }

        let style = null;
        if (layerDetail.tableType === FeatureType.GeneralTrafficLocation) {
          switch (layerDetail.dataType) {
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.roadCategory:
              style = "t_general_traffic_location_base_road_category";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.laneCount:
              style = "t_general_traffic_location_base_lane_count";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.day12hVolume:
              style = "t_general_traffic_location_base_12h_volume";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.day24hVolume:
              style = "t_general_traffic_location_base_24h_volume";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.congestionLevel:
              style = "t_general_traffic_location_base_congestion_level";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.morningEveningSpeed:
              style = "v_general_traffic_location_base_morning_evening_speed";
              break;
            case GENERAL_TRAFFIC_LOCTAION_DATA_TYPE_LIST.daytimeSpeed:
              style = "v_general_traffic_location_base_daytime_speed";
              break;
          }
        }
        layerKeys.push(layerKey);
        cqlParameters.push(cqlParameter);
        styles.push(style);
      });
    }
    if (cqlParameters.length == 0) {
      this._wmsLayer.setVisible(false);
    } else {
      this._wmsImageSource.updateParams({
        LAYERS: layerKeys.join(","),
        EXCEPTIONS: "application/json",
        CQL_FILTER: cqlParameters.join(";"),
        STYLES: styles.join(","),
      });
      this._wmsLayer.setVisible(isVisible);
    }
  }

  /**
   * 表示中のレイヤ数を取得
   */
  getVisibleLayerCount() {
    return this._wmsLayer?.getVisible() ? this._visibleLayers.length : 0;
  }

  /**
   * 表示レイヤが存在するか判定
   */
  isVisibleLayer(_map: Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer);
  }
}
